<script setup lang="ts">
/**
 * NASTAVENÍ → MODULY: what this venue uses, inside what its plan sells.
 *
 * A kind of venue (preset) is a starting selection; each module is a switch.
 * The core is shown and cannot be switched; a module the plan does not sell is
 * shown, disabled, and says so; a preview module is shown as coming. Owner and
 * admin save; others read. Saving re-signs the venue's bundle on the server,
 * so tills and kitchen screens change in seconds — and the office's own
 * navigation changes at once (`changed`).
 */
import { computed, onMounted, ref } from "vue";
import type { ConsoleApi, VenueModules } from "../api";

const props = defineProps<{
  api: Pick<ConsoleApi, "modules" | "saveModules">;
  me: { roleId: string } | null;
  t: (key: string) => string;
}>();

const emit = defineEmits<{ changed: [effective: readonly string[]] }>();

const view = ref<VenueModules | null>(null);
const chosen = ref<Set<string>>(new Set());
const status = ref<"loading" | "ready" | "failed">("loading");
const busy = ref(false);
const notice = ref<{ tone: "ok" | "bad"; text: string } | null>(null);

const canConfigure = computed(() => props.me === null || ["owner", "admin"].includes(props.me.roleId));

const load = async (): Promise<void> => {
  try {
    view.value = await props.api.modules();
    chosen.value = new Set(view.value.selection);
    status.value = "ready";
  } catch {
    status.value = "failed";
  }
};
onMounted(load);

const sold = (key: string): boolean => view.value?.plan.modules.includes(key) === true;
const tierOf = (key: string) => view.value?.catalogue.find((m) => m.key === key)?.tier;
const switchable = (key: string): boolean => {
  const tier = tierOf(key);
  return tier === "service" || (tier === "optional" && sold(key));
};

/** Which preset the current choice is, exactly, if any. */
const presetNow = computed(() => {
  const picked = [...chosen.value].sort().join(",");
  return view.value?.presets.find((p) => [...p.modules].sort().join(",") === picked)?.id ?? null;
});
const hasServiceMode = computed(() => chosen.value.has("tables") || chosen.value.has("counter"));
const dirty = computed(() => {
  const before = [...(view.value?.selection ?? [])].sort().join(",");
  return before !== [...chosen.value].sort().join(",");
});

const toggle = (key: string, on: boolean): void => {
  const next = new Set(chosen.value);
  if (on) next.add(key);
  else next.delete(key);
  chosen.value = next;
  notice.value = null;
};

/** A preset, cut to what the plan sells — the server refuses anything else. */
const applyPreset = (id: string): void => {
  const preset = view.value?.presets.find((p) => p.id === id);
  if (!preset) return;
  chosen.value = new Set(preset.modules.filter((k) => switchable(k)));
  notice.value = null;
};

const requirementNames = (key: string): string =>
  (view.value?.catalogue.find((m) => m.key === key)?.requires ?? []).map((r) => props.t(`module.${r}.name`)).join(", ");

const save = async (): Promise<void> => {
  if (!hasServiceMode.value || busy.value) return;
  busy.value = true;
  notice.value = null;
  try {
    view.value = await props.api.saveModules({ selection: [...chosen.value] });
    chosen.value = new Set(view.value.selection);
    notice.value = { tone: "ok", text: props.t("modules.saved") };
    emit("changed", view.value.effective);
  } catch (e) {
    notice.value = { tone: "bad", text: e instanceof Error && e.message ? e.message : props.t("common.failed") };
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <form class="gf-panel modules" data-testid="modules-panel" @submit.prevent="save">
    <h2 class="gf-panel__title">{{ t("modules.title") }}</h2>
    <p class="gf-muted modules__lead">{{ t("modules.lead") }}</p>

    <p v-if="status === 'loading'" class="gf-muted" role="status">{{ t("common.loading") }}</p>
    <p v-else-if="status === 'failed'" class="gf-note gf-note--bad" role="alert">{{ t("common.failed") }}</p>

    <template v-else-if="view">
      <p class="modules__plan">
        <span class="gf-strong">{{ view.plan.planId ? t("modules.plan").replace("{plan}", view.plan.planId) : t("modules.noPlan") }}</span>
        <span v-if="view.plan.lapsed" class="gf-chip gf-chip--warn">{{ t("modules.planLapsed") }}</span>
      </p>
      <p v-if="!canConfigure" class="gf-note" role="note">{{ t("venue.ownerOnly") }}</p>

      <div class="modules__presets" role="group" :aria-label="t('modules.preset')">
        <span class="gf-label">{{ t("modules.preset") }}</span>
        <div class="modules__presetRow">
          <button
            v-for="preset in view.presets"
            :key="preset.id"
            type="button"
            class="gf-button gf-button--ghost"
            :class="{ 'gf-button--selected': presetNow === preset.id }"
            :aria-pressed="presetNow === preset.id"
            :data-preset="preset.id"
            :disabled="!canConfigure || busy"
            @click="applyPreset(preset.id)"
          >
            {{ t(`preset.${preset.id}`) }}
          </button>
          <span v-if="!presetNow" class="gf-chip">{{ t("modules.custom") }}</span>
        </div>
      </div>

      <ul class="modules__list">
        <li v-for="m in view.catalogue" :key="m.key" class="modules__item" :data-module="m.key">
          <label class="modules__switch">
            <input
              type="checkbox"
              :checked="m.tier === 'core' || chosen.has(m.key)"
              :disabled="!canConfigure || busy || !switchable(m.key)"
              @change="toggle(m.key, ($event.target as HTMLInputElement).checked)"
            />
            <span class="modules__name">{{ t(`module.${m.key}.name`) }}</span>
          </label>
          <span v-if="m.tier === 'core'" class="gf-chip">{{ t("modules.always") }}</span>
          <span v-else-if="m.tier === 'preview'" class="gf-chip">{{ t("modules.soon") }}</span>
          <span v-else-if="m.tier === 'optional' && !sold(m.key)" class="gf-chip gf-chip--warn">{{ t("modules.notInPlan") }}</span>
          <span v-else-if="chosen.has(m.key) && !view.effective.includes(m.key) && m.requires.length > 0" class="gf-chip gf-chip--warn">
            {{ t("modules.needs").replace("{modules}", requirementNames(m.key)) }}
          </span>
          <p class="gf-muted modules__desc">{{ t(`module.${m.key}.lead`) }}</p>
        </li>
      </ul>

      <p v-if="!hasServiceMode" class="gf-note gf-note--bad" role="alert">{{ t("modules.noServiceMode") }}</p>
      <p v-if="notice" class="gf-note" :class="{ 'gf-note--bad': notice.tone === 'bad' }" role="status">{{ notice.text }}</p>
      <div v-if="canConfigure">
        <button type="submit" class="gf-button gf-button--primary" data-testid="save-modules" :disabled="busy || !dirty || !hasServiceMode">
          {{ t("modules.save") }}
        </button>
      </div>
    </template>
  </form>
</template>

<style scoped>
.modules {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.modules__lead,
.modules__plan {
  margin: 0;
}

.modules__plan {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-2);
  align-items: center;
}

.modules__presets {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
}

.modules__presetRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-2);
  align-items: center;
}

.modules__list {
  display: grid;
  gap: var(--gf-space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.modules__item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0 var(--gf-space-3);
  align-items: center;
  padding: var(--gf-space-2) 0;
  border-bottom: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.modules__switch {
  display: inline-flex;
  gap: var(--gf-space-2);
  align-items: center;
  min-height: 32px;
}

.modules__name {
  font-weight: var(--gf-weight-medium);
}

.modules__desc {
  grid-column: 1 / -1;
  margin: 0;
  font-size: var(--gf-text-sm);
}
</style>
